import { membershipDescriptionFor, ownerDescriptionFor } from 'groups/lib/groupDisplay';
import BoxBody from 'components/BoxBody';
import Dialog from 'components/Dialog';
import Group from 'entities/Group';
import GroupAvatar from 'groups/components/GroupAvatar';
import GroupCreateModal from 'groups/components/GroupCreateModal';
import GroupName from 'groups/components/GroupName';
import Groups from 'collections/Groups';
import Input from 'components/Input';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import Link from 'components/Link';
import MediaObject from 'components/MediaObject';
import ModalTrigger from 'components/ModalTrigger';
import * as React from 'react';
import ScrollableArea from 'components/ScrollableArea';
import Text from 'components/Text';

import './styles/GroupSelectionDialog.scss';

type Props = {
  groups: Groups | Group[];
  query: string;
  onSelect: (group: Group) => void;
  onQueryChange: (value: string) => void;
  onRequestHide?: () => void;
  title: string;
  emptyMessage: string;
  loading: boolean;
};

class GroupSelectionDialog extends React.Component<Props> {
  static defaultProps = {
    loading: false,
    onSelect: (group: Group) => {},
    onQueryChange: (value: string) => {},
    query: '',
  };

  renderAddGroupOption = () => {
    const dialog = <GroupCreateModal onCreate={this.props.onSelect} onRequestHide={() => {}} />;

    return (
      <ModalTrigger modal={dialog}>
        <Link
          className="GroupSelectionDialog-addGroupItem-link"
          data-test-selector="group-selection-dialog-add-group-option"
          eventElementName="GroupSelectionDialog-addGroupItem-link"
        >
          <Text size="xsmall" weight="semibold">
            Create a class
          </Text>
        </Link>
      </ModalTrigger>
    );
  };

  renderGroupOption = (group: Group) => {
    const subscribersCount = membershipDescriptionFor(group);

    const groupCodeOpt = !group.isStaffClass() && (
      <Text className="GroupSelectionDialog-groupItem-code" color="graphite">
        @{group.getCodeOrError()}
      </Text>
    );

    return (
      <li key={group.id} className="GroupSelectionDialog-groupItem">
        <Link
          className="GroupSelectionDialog-groupItem-link"
          data-test-selector={`group-selection-dialog-add-group-item-${group.id}`}
          onClick={() => this.props.onSelect(group)}
          eventElementName="GroupSelectionDialog-groupItem-link"
        >
          <MediaObject center figure="right">
            <MediaObject center figure="left" gutter="small">
              <GroupAvatar className="GroupSelectionDialog-groupItem-avatar" group={group} />
              <div>
                <Text className="GroupSelectionDialog-groupItem-name" truncate>
                  <GroupName group={group} />
                </Text>
                <MediaObject center figure="left" gutter="xxsmall">
                  {groupCodeOpt}
                  <Text truncate className="GroupSelectionDialog-groupItem-owners" color="graphite">
                    {`| ${ownerDescriptionFor(group)}`}
                  </Text>
                </MediaObject>
              </div>
            </MediaObject>
            <Text className="GroupSelectionDialog-groupItem-count" color="graphite" size="xxsmall">
              {subscribersCount}
            </Text>
          </MediaObject>
        </Link>
      </li>
    );
  };

  renderNoGroups = () => {
    return <li className="GroupSelectionDialog-emptyMessage">{this.props.emptyMessage}</li>;
  };

  handleQueryChange = (event: React.SyntheticEvent) => {
    const { onQueryChange } = this.props;
    const target = event.target as HTMLInputElement;
    onQueryChange(target.value);
  };

  renderGroupList = () => {
    const { groups, loading } = this.props;
    const groupsArray = groups instanceof Groups ? groups.toArray() : groups;

    return (
      <ScrollableArea className="GroupSelectionDialog-scrollArea" showShadows useBodyChildrenHeight>
        {loading ? (
          <CenteredLoadingIndicator />
        ) : (
          <ul className="GroupSelectionDialog-list">
            {groupsArray.length === 0 && this.renderNoGroups()}
            {groupsArray.map((group: Group) => this.renderGroupOption(group))}
          </ul>
        )}
      </ScrollableArea>
    );
  };

  render() {
    const { query, ...other } = this.props;

    return (
      <Dialog
        eventScreenName="GroupSelectionDialog"
        {...other}
        className="GroupSelectionDialog"
        size="small"
        data-testid="group-selection-dialog"
      >
        <BoxBody className="GroupSelectionDialog-body" divided full>
          <React.Fragment>
            <div className="GroupSelectionDialog-addGroupSection">
              {this.renderAddGroupOption()}
            </div>
            <div className="GroupSelectionDialog-queryInput">
              <Input
                onChange={this.handleQueryChange}
                placeholder={'Search all classes by class name'}
                value={query}
              />
            </div>
            {this.renderGroupList()}
          </React.Fragment>
        </BoxBody>
      </Dialog>
    );
  }
}

export default GroupSelectionDialog;
